/**
 * Final-evaluation unit renderer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { getCorrectProblemBlockCount, getGradeCutoff, getGradeForBlock } from "utils/grade.util";
import ProblemMock from "pages/board/problems/ProblemMock";
import FinalPassport from "./FinalPassport";
import FinalFirst from "./FinalFirst";
import FinalSuccess from "./FinalSuccess";
import FinalRetry from "./FinalRetry";
import FinalFailure from "./FinalFailure";
import { actionCreateCertificate } from "context/action";
import { getSectionTitle } from "utils/course";
import { useAsterController } from "context";
import { getBrighterColor } from "utils/string";

/**
 * Orchestrates final-exam intro, attempt, passport, success, and failure screens.
 *
 * @param {{ xblocks?: Array, onSubmitProblem?: Function }} props
 */
const FinalEvaluationRender = (props) => {
  const { xblocks, onSubmitProblem, onReSubmitProblems, setStackedAnswers } = props;

  const { t } = useTranslation("common");
  const { courseId, sectionIndex } = useParams();

  const [controller, dispatch] = useAsterController();
  const { courses, structure: courseStructure, themes } = controller;

  const colorMain1 = (themes || []).find((v) => v.name === "color_main1")?.value || "#F76060";
  const currCourse = useMemo(() => (courses.data || []).find((v) => v.id === courseId) || {}, [courses, courseId]);

  const [cursor, setCursor] = useState(-2);
  const [problemBlocks, setProblemBlocks] = useState([]);
  const [originCorrectCount, setOriginCorrectCount] = useState(0);
  const [badgeEarned, setBadgeEarned] = useState(false);
  const [retryBadgeEarned, setRetryBadgeEarned] = useState(false);

  /**
   * Counts correct final-exam blocks against the course cutoff.
   *
   * @returns {number}
   */
  const setBadgeStatus = useCallback(() => {
    const correctCount = getCorrectProblemBlockCount(
      currCourse,
      problemBlocks.map((p) => p.id)
    )["counter"];
    const cutoff = getGradeCutoff(currCourse);
    const total = problemBlocks.length;

    if (total * cutoff < correctCount) {
      // If this is the first time to pass this module, we register this module
      // if (cursor + 1 === problemBlocks.length && !badgeEarned) {
      actionCreateCertificate(dispatch, {
        course_id: courseId,
        course_title: currCourse.display_name,
        module_title: getSectionTitle(courseStructure, sectionIndex),
        is_final: true,
        total: currCourse.moduleMapper ? currCourse.moduleMapper.filter((v) => v.gradeFormat && v.gradeFormat.toLowerCase() === "quiz").length : 0,
        is_save: true,
      });
      // }

      setBadgeEarned(true);
      setOriginCorrectCount(correctCount);
      setRetryBadgeEarned(true);
      setCursor(total);
    } else {
      setRetryBadgeEarned(false);
    }

    return correctCount;
  }, [courseId, courseStructure, currCourse, dispatch, problemBlocks, sectionIndex]);

  // Set the badge earned state
  useEffect(() => {
    if ((courses.data || []).length && problemBlocks.length && cursor === -2) {
      setOriginCorrectCount(setBadgeStatus());
    }
  }, [(courses.data || []).length, problemBlocks.length, cursor, setBadgeStatus]);

  useEffect(() => {
    setProblemBlocks(xblocks.filter((b) => b.type === "problem").sort(() => 0.5 - Math.random()));
  }, [xblocks]);

  // If continue button clicked
  /**
   * Advances the final-evaluation flow after checking scores.
   *
   * @returns {void}
   */
  const onContinueClick = () => {
    // If the next page if final evaluation page
    if (cursor + 1 === problemBlocks.length) {
      const currCorrectCount = setBadgeStatus();

      // If retry grade is above than origin grade, then submit this time answers to DB
      if (currCorrectCount > originCorrectCount && badgeEarned) {
        onReSubmitProblems();
      }
    }
    setCursor(cursor + 1);
  };

  /**
   * Restarts the final evaluation.
   *
   * @returns {void}
   */
  const onRestartClick = () => {
    setCursor(0);
    setStackedAnswers([]);
    setRetryBadgeEarned(false);
    setProblemBlocks(xblocks.filter((b) => b.type === "problem").sort(() => 0.5 - Math.random()));
  };

  /**
   * Forwards a final-exam answer to the parent submit handler.
   *
   * @param {Object} payload Answer payload.
   * @returns {void}
   */
  const onMockProblemSubmit = (payload) => {
    if (badgeEarned) {
      // If it's success retry mode, then do not allow post and store answers to temperary variable
      onSubmitProblem(payload, "NOT_ALLOW_POST");
    } else {
      onSubmitProblem(payload, "ALLOW_POST");
    }
  };

  return (
    <>
      {cursor === -2 && <FinalPassport totalCount={problemBlocks.length} cutoff={getGradeCutoff(currCourse)} onContinueClick={onContinueClick} />}
      {cursor === -1 && <FinalFirst onContinueClick={onContinueClick} />}
      {0 <= cursor && cursor < problemBlocks.length && (
        <>
          <div className="text-xs mt-4 text-uppercase">
            {t("Question")}&nbsp;
            {cursor + 1}/{problemBlocks.length}
          </div>
          <div className="flex mt-2 bg-gray-200 w-full rounded mb-4" style={{ height: 10 }}>
            <div style={{ width: `${((cursor + 1) * 100) / problemBlocks.length}%`, backgroundImage: `linear-gradient(to right, ${getBrighterColor(colorMain1, 50)}, ${colorMain1})` }} className="rounded"></div>
          </div>
          <ProblemMock key={cursor} rawText={problemBlocks[cursor].html} grade={getGradeForBlock(currCourse, problemBlocks[cursor].id)} courseId={courseStructure.course_id} blockId={problemBlocks[cursor].id} onSubmitProblem={onMockProblemSubmit} onContinueClick={onContinueClick} />
        </>
      )}
      {cursor === problemBlocks.length && badgeEarned && retryBadgeEarned && <FinalSuccess onRestartClick={onRestartClick} />}
      {cursor === problemBlocks.length && badgeEarned && !retryBadgeEarned && (
        <FinalRetry
          onRestartClick={onRestartClick}
          totalCount={problemBlocks.length}
          successCount={
            getCorrectProblemBlockCount(
              currCourse,
              problemBlocks.map((p) => p.id)
            )["counter"]
          }
        />
      )}
      {cursor === problemBlocks.length && !badgeEarned && (
        <FinalFailure
          onRestartClick={onRestartClick}
          totalCount={problemBlocks.length}
          successCount={
            getCorrectProblemBlockCount(
              currCourse,
              problemBlocks.map((p) => p.id)
            )["counter"]
          }
        />
      )}
    </>
  );
};

export default FinalEvaluationRender;
